import React, { useState, useEffect, useContext } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    PieChart, Pie, Cell
} from 'recharts';
import { TrendingUp, TrendingDown, Target, ArrowUpRight, Filter, Loader2, ShoppingBag } from 'lucide-react';
import { StatCard } from '../components/StatCard';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

const Analytics = () => {
    const { user } = useContext(AuthContext);
    console.log("Authenticated User:", user.company_name); // Debugging line to verify user context
    const [data, setData] = useState({ summary: { revenue: 0, profit: 0, margin: 0, pending: 0, vendor_pending: 0 }, projects: [], expenses: [] });
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState('6');

    useEffect(() => {
        fetchAnalytics();
    }, [timeframe]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const res = await api.get(`get_analytics.php?months=${timeframe}`);
            setData(res.data);
        } catch (err) {
            console.error("Analytics Sync Failed", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="h-96 flex flex-col items-center justify-center text-slate-400 font-black italic uppercase tracking-widest">
            <Loader2 className="animate-spin mb-4 text-indigo-600" size={48} />
            Calculating Net Margins...
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">Financial Intelligence</h1>
                    <p className="text-slate-500 font-medium mt-1 uppercase text-[10px] tracking-widest font-black">{`${user.company_name} Audit Mode`}</p>
                </div>
                <div className="flex gap-2 bg-white border border-slate-200 p-1.5 rounded-2xl shadow-sm">
                    {['3', '6', '12'].map((m) => (
                        <button
                            key={m}
                            onClick={() => setTimeframe(m)}
                            className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${timeframe === m ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            {m} Months
                        </button>
                    ))}
                </div>
            </div>

            {/* REAL-TIME STAT CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                    title="Gross Revenue"
                    value={`₹${(data.summary.revenue / 100000).toFixed(1)}L`}
                    sub="Realized Income"
                    color="bg-indigo-600"
                    icon={<TrendingUp size={20} />}
                />
                <StatCard
                    title="True Net Profit"
                    value={`₹${(data.summary.profit / 100000).toFixed(1)}L`}
                    sub={`${data.summary.margin}% Project Margin`}
                    color={data.summary.profit >= 0 ? "bg-emerald-600" : "bg-rose-600"}
                    icon={<Target size={20} />}
                />
                <StatCard
                    title="Receivables"
                    value={`₹${(data.summary.pending / 100000).toFixed(1)}L`}
                    sub="Unpaid Client Bills"
                    color="bg-amber-500"
                    icon={<TrendingDown size={20} />}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* COMPREHENSIVE SITE PROFITABILITY */}
                <div className="bg-white p-10 rounded-[48px] border border-slate-100 shadow-sm">
                    <h3 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-2">
                        <TrendingUp size={20} className="text-indigo-600" /> Comprehensive Site Performance
                    </h3>
                    <div className="h-[350px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.projects} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 'bold' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} />
                                <Tooltip cursor={{ fill: '#F8FAFF' }} contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '20px' }} />
                                <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '30px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase' }} />
                                <Bar dataKey="income" fill="#10B981" radius={[10, 10, 0, 0]} barSize={20} name="Income" />
                                <Bar dataKey="expense" fill="#6366F1" radius={[10, 10, 0, 0]} barSize={20} name="Full Cost" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* DETAILED SPEND ALLOCATION */}
                <div className="bg-white p-10 rounded-[48px] border border-slate-100 shadow-sm">
                    <h3 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-2">
                        <ShoppingBag size={20} className="text-amber-500" /> Cost Breakdown
                    </h3>
                    <div className="h-[350px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data.expenses}
                                    innerRadius={90}
                                    outerRadius={130}
                                    paddingAngle={10}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {data.expenses.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: '16px', border: 'none' }} />
                                <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* UPDATED STRATEGY FOOTER */}
            <div className="bg-slate-900 rounded-[48px] p-10 text-white flex items-center justify-between relative overflow-hidden shadow-2xl">
                <div className="relative z-10">
                    <p className="text-indigo-400 font-black text-[10px] uppercase tracking-[0.3em]">Financial Health Check</p>
                    <h4 className="text-3xl font-black mt-2 italic">
                        Liquidity Gap: <span className={data.summary.revenue > data.summary.vendor_pending ? "text-emerald-400" : "text-rose-400"}>₹{(data.summary.revenue - data.summary.vendor_pending).toLocaleString()}</span>
                    </h4>
                    <p className="text-slate-400 mt-2 text-xs font-bold uppercase tracking-tight max-w-lg leading-relaxed">
                        Full Costing includes Vendor Payments + Petty Cash + On-site Material Purchases. Your actual margin is calculated after all overheads.
                    </p>
                </div>
                <button onClick={fetchAnalytics} className="bg-white text-slate-900 p-6 rounded-[32px] hover:bg-indigo-500 hover:text-white transition-all shadow-xl group">
                    <ArrowUpRight className="group-hover:rotate-45 transition-transform" size={32} />
                </button>
            </div>
        </div>
    );
};

export default Analytics;